/**
 * Select - 드롭다운 선택 컴포넌트
 *
 * Portal 기반 드롭다운으로 옵션을 선택한다. 키보드 내비게이션 및 검색 기능 지원.
 * @param searchable - true이면 옵션 검색 입력 필드 표시
 * @param options - 선택 항목 배열 (value, label, icon, avatar, status 등)
 */
'use client';

import React, { useState, useRef, useEffect, useCallback, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';
import styles from './Select.module.css';

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  description?: string;
  disabled?: boolean;
  avatar?: string;
  status?: 'online' | 'offline' | 'busy' | 'error' | 'special';
  rightBadge?: string;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  helperText?: string;
  searchable?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function Select({
  options,
  value,
  onChange,
  placeholder = '선택하세요',
  label,
  error,
  helperText,
  searchable = false,
  disabled = false,
  size = 'md',
  className = '',
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerBtnRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Stable ID prefix for ARIA option IDs and listbox ID
  const listboxBaseId = useId();

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(lower) ||
        (opt.description && opt.description.toLowerCase().includes(lower)),
    );
  }, [options, search]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
      setSearch('');
      setDropdownPos(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search when opened
  useEffect(() => {
    if (open && searchable && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open, searchable]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[data-option]');
    items[highlightIndex]?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  const toggleOpen = useCallback(() => {
    if (disabled) return;
    setOpen((prev) => {
      if (prev) {
        setSearch('');
        setDropdownPos(null);
      } else if (triggerBtnRef.current) {
        const rect = triggerBtnRef.current.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
      return !prev;
    });
    setHighlightIndex(-1);
  }, [disabled]);

  const selectOption = useCallback(
    (opt: SelectOption) => {
      if (opt.disabled) return;
      onChange(opt.value);
      setOpen(false);
      setSearch('');
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          if (triggerBtnRef.current) {
            const rect = triggerBtnRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
          }
          setOpen(true);
          setHighlightIndex(0);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightIndex((prev) => {
            const next = prev + 1;
            return next >= filteredOptions.length ? 0 : next;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightIndex((prev) => {
            const next = prev - 1;
            return next < 0 ? filteredOptions.length - 1 : next;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightIndex >= 0 && highlightIndex < filteredOptions.length) {
            selectOption(filteredOptions[highlightIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          setSearch('');
          break;
      }
    },
    [open, highlightIndex, filteredOptions, selectOption],
  );

  const hasError = Boolean(error);
  const inputId = label ? `select-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined;
  const listboxId = `${listboxBaseId}-listbox`;
  const getOptionId = (idx: number) => `${listboxBaseId}-option-${idx}`;

  const sizeClass =
    size !== 'md' ? styles[`size${size.charAt(0).toUpperCase()}${size.slice(1)}`] : '';

  const triggerClasses = [
    styles.trigger,
    hasError ? styles.triggerError : '',
    open ? styles.triggerOpen : '',
    disabled ? styles.triggerDisabled : '',
    sizeClass,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`${styles.formGroup} ${className}`} ref={wrapperRef} onKeyDown={handleKeyDown}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      )}
      <button
        ref={triggerBtnRef}
        type="button"
        id={inputId}
        className={triggerClasses}
        onClick={toggleOpen}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={
          open && highlightIndex >= 0 ? getOptionId(highlightIndex) : undefined
        }
        data-invalid={hasError || undefined}
        aria-describedby={
          inputId
            ? hasError
              ? `${inputId}-error`
              : helperText
                ? `${inputId}-helper`
                : undefined
            : undefined
        }
      >
        <span className={selectedOption ? styles.triggerText : styles.triggerPlaceholder}>
          {selectedOption ? (
            <span className={styles.selectedDisplay}>
              {selectedOption.avatar && (
                <img src={selectedOption.avatar} alt="" className={styles.selectedAvatar} />
              )}
              {!selectedOption.avatar && selectedOption.icon && (
                <span className={styles.selectedIcon}>{selectedOption.icon}</span>
              )}
              {selectedOption.status && (
                <span
                  className={`${styles.statusDot} ${styles[`status-${selectedOption.status}`]}`}
                />
              )}
              {selectedOption.label}
            </span>
          ) : (
            placeholder
          )}
        </span>
        <ChevronDown size={16} className={`${styles.caretIcon} ${open ? styles.caretOpen : ''}`} />
      </button>

      {open &&
        dropdownPos &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            id={listboxId}
            className={styles.dropdown}
            role="listbox"
            aria-label={label ?? placeholder}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
            }}
            onDragStart={(e) => e.preventDefault()}
          >
            {searchable && (
              <div className={styles.searchWrapper}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  ref={searchRef}
                  type="text"
                  className={styles.searchInput}
                  placeholder="검색..."
                  aria-label="검색"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setHighlightIndex(0);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div className={styles.optionsList} ref={listRef}>
              {filteredOptions.length === 0 ? (
                <div className={styles.noResults}>검색 결과가 없습니다</div>
              ) : (
                filteredOptions.map((opt, idx) => (
                  <div
                    key={opt.value}
                    id={getOptionId(idx)}
                    data-option
                    role="option"
                    aria-selected={opt.value === value}
                    aria-disabled={opt.disabled}
                    className={[
                      styles.option,
                      opt.value === value ? styles.optionSelected : '',
                      opt.disabled ? styles.optionDisabled : '',
                      idx === highlightIndex ? styles.optionHighlighted : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => selectOption(opt)}
                    onMouseEnter={() => setHighlightIndex(idx)}
                  >
                    <span className={styles.checkColumn}>
                      {opt.value === value && <Check size={14} className={styles.checkIcon} />}
                    </span>
                    {opt.avatar && <img src={opt.avatar} alt="" className={styles.optionAvatar} />}
                    {!opt.avatar && opt.icon && (
                      <span className={styles.optionIcon}>{opt.icon}</span>
                    )}
                    {opt.status && (
                      <span className={`${styles.statusDot} ${styles[`status-${opt.status}`]}`} />
                    )}
                    <div className={styles.optionContent}>
                      <span className={styles.optionLabel}>{opt.label}</span>
                      {opt.description && (
                        <span className={styles.optionDescription}>{opt.description}</span>
                      )}
                    </div>
                    {opt.rightBadge && <span className={styles.rightBadge}>{opt.rightBadge}</span>}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body,
        )}

      {hasError && (
        <p className={styles.errorText} id={inputId ? `${inputId}-error` : undefined} role="alert">
          {error}
        </p>
      )}
      {!hasError && helperText && (
        <p className={styles.helperText} id={inputId ? `${inputId}-helper` : undefined}>
          {helperText}
        </p>
      )}
    </div>
  );
}
